import { StandardEvents } from '@shopify/events';
import { isLowPowerDevice, requestIdleCallback } from '@theme/utilities';

/**
 * Plays the 3D particle burst from a product page's add-to-cart button when an
 * add starts, in step with Horizon's own button animation. If the add fails,
 * the burst is cancelled and its particles shrink away quickly.
 *
 * This module stays small and never imports the 3D runtime itself. The effect
 * (`@theme/particle-burst`) is imported once the shopper is likely to add to
 * cart, and only when the device and preferences allow it. The cart flow,
 * button state, and live-region announcement are left entirely to
 * product-form.js; a burst that isn't ready in time is skipped, never queued.
 *
 * The model URLs come from the `data-particle-burst-models` JSON attribute on
 * the script tag that loads this module (see snippets/scripts.liquid).
 */

/** Product forms inside these containers don't trigger the burst. */
const EXCLUDED_CONTAINERS = '.quick-add-modal, quick-add-component, product-card';
const BUTTON_SELECTOR = '[ref="addToCartButton"]';
/** Start loading when a button is this close to the viewport. */
const PREPARE_MARGIN = '200px';

/** @typedef {typeof import('./particle-burst.js')} ParticleBurstModule */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const config = /** @type {HTMLScriptElement | null} */ (document.querySelector('script[data-particle-burst-models]'));
const models = { urls: parseUrls(config?.dataset.particleBurstModels) };

/** @type {Promise<unknown> | null} */
let loading = null;
/** @type {ParticleBurstModule | null} */
let effect = null;
/** @type {IntersectionObserver | null} */
let observer = null;

function isAllowed() {
  const connection = /** @type {{ saveData?: boolean } | undefined} */ (
    /** @type {any} */ (navigator).connection
  );
  return (
    models.urls.length > 0 &&
    !reducedMotion.matches &&
    !connection?.saveData &&
    !isLowPowerDevice() &&
    typeof WebGL2RenderingContext !== 'undefined'
  );
}

/** Loads and prepares the effect once. Failures are not retried. */
function prepare() {
  if (loading || !isAllowed()) return;
  observer?.disconnect();
  observer = null;

  loading = import('@theme/particle-burst')
    .then(async (module) => {
      await module.prepare(models);
      if (reducedMotion.matches) {
        module.dispose();
        loading = null;
        return;
      }
      effect = module;
    })
    .catch(() => {
      // The burst is optional; the add-to-cart flow is unaffected.
    });
}

/**
 * Returns the button the shopper used for an add from this product form, or
 * null when the form isn't one that should burst.
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function sourceButton(target) {
  if (!(target instanceof Element)) return null;
  const form = target.closest('product-form-component');
  if (!form || form.closest(EXCLUDED_CONTAINERS)) return null;

  const button = form.querySelector(BUTTON_SELECTOR);
  // The sticky bar adds through the main form, marking its button as a
  // puppet until the request settles, so check this when the event fires.
  if (button instanceof HTMLElement && button.dataset.puppet === 'true') {
    return document.querySelector(`sticky-add-to-cart ${BUTTON_SELECTOR}`) ?? button;
  }
  return button;
}

/** @param {Event} event */
function isAddToCartButton(event) {
  return event.target instanceof Element && event.target.closest(BUTTON_SELECTOR) !== null;
}

/** @param {Event} event */
function onCartLinesUpdate(event) {
  const update = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
  if (update.action !== 'add' || !effect) return;
  const source = sourceButton(event.target);
  const burst = source && effect.burstFromElement(source);
  if (!burst) return;

  // Cart errors are handled and announced by the product form; the burst
  // only needs to be retracted.
  update.promise?.then((result) => result?.detail?.didError && burst.cancel(), burst.cancel);
}

/** @param {Event} event */
function onIntent(event) {
  if (isAddToCartButton(event)) prepare();
}

function observeButtons() {
  if (loading || !isAllowed()) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) prepare();
    },
    { rootMargin: PREPARE_MARGIN }
  );
  for (const button of document.querySelectorAll(BUTTON_SELECTOR)) {
    if (sourceButton(button.closest('product-form-component')) || button.closest('sticky-add-to-cart')) {
      observer.observe(button);
    }
  }
}

/** @param {string | undefined} value */
function parseUrls(value) {
  try {
    const urls = JSON.parse(value ?? '[]');
    return Array.isArray(urls) ? urls.filter((url) => typeof url === 'string') : [];
  } catch {
    return [];
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
document.addEventListener('pointerdown', onIntent, { capture: true, passive: true });
document.addEventListener('focusin', onIntent);

reducedMotion.addEventListener('change', () => {
  if (!reducedMotion.matches || !effect) return;
  effect.dispose();
  effect = null;
  loading = null;
});

// Wait until the page has loaded before looking for buttons to prepare.
if (document.readyState === 'complete') {
  requestIdleCallback(observeButtons);
} else {
  addEventListener('load', () => requestIdleCallback(observeButtons), { once: true });
}
